import { Dispatch } from 'redux';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@hooks/index';
import { ROOT_STATE } from '@store/store.types';
import { getViewDetailUrl, refreshTicketList, statusChanged } from '../../constants/ticketlist.utils';
import { configs } from '@constants/configs/configs';
import { handleApiResponse, useApiHandler } from '../../hooks/useApiHandler';
import { IuseActionPopoverProps } from '../../constants/ticketlist.interface';
import { TICKET_LIST_ACTION_TYPES } from '@constants/actionTypes/tikcetListActionTypes';
import {
    CompleteTicketAction,
    taskStatusTypes,
    ticketCategoryOptions,
    validateTypes,
} from '../../constants/ticketlist.constants';

export const useActionPopover = (props: IuseActionPopoverProps) => {
    const {
        onCompleteTicketOpen,
        onCompleteVerifyTicketOpen,
        onReassignTicketOpen,
        onCancelTicketOpen,
        onReopenTicketOpen,
    } = props;

    const intl = useIntl();

    const {
        ticketAttachmentsApi,
        userTicketTaskListAPI,
        encTaskAPI,
        claimTaskAPI,
        allUsersForQueueAPI,
        getTaskFromPropertiesAPI,
        ticketActiveTaskAPI,
        defaultListAPI,
        listAPI,
        filteredListAPI,
    } = useApiHandler();
    const dispatch: Dispatch<any> = useAppDispatch();

    const {
        tickets: { ticketListJson },
        controller: { isSimplifiedView, isArchivedAllowed, isAssignAllowed },
        modalFlags,
        modalData,
        ticketDetail: { tkmWorkflows, tkmQueues, quickAction },
        listApiType,
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList);

    const { validateComments } = modalData;
    const ticketDataFlags = {
        ticketId: '',
        ticketIdVerify: '',
        validateComments: '',
        validateVerifyComments: '',
        actTask: '',
        tkmTaskId: '',
        actProInstId: '',
        taskVerifyName: '',
        activitiTaskId: '',
        action: '',
        internalComments: false,
        taskNameContent: '',
        isCommentRequired: false,
        taskHeader: '',
        ticketListtaskCompleteBtn: '',
        reassignHeader: '',
        taskId: '',
        taskClaimed: false,
        queueId: '',
        assignee: '',
        processInstanceId: '',
    };

    const updateClaimedTicket = (ticketId: string) => {
        const index = ticketListJson.findIndex(ticket => ticket.encryptedId === ticketId);
        if (index !== -1) {
            const updatedTickets = [...ticketListJson];
            updatedTickets[index] = {
                ...updatedTickets[index],
                status: 'Open',
                taskStatus: 'Claimed',
            };
            updatedTickets[index] = statusChanged(
                updatedTickets[index],
                isSimplifiedView,
                isArchivedAllowed,
                isAssignAllowed,
            );
            dispatch({ type: TICKET_LIST_ACTION_TYPES.UPDATE_TICKETS, payload: updatedTickets });
        }
    };

    const claimTask = async (taskId: string, activeTaskId: string) => {
        const taskObject = { ActivitiTaskId: activeTaskId, TaskId: taskId, TicketId: ticketDataFlags.ticketId };
        handleApiResponse(
            claimTaskAPI,
            { taskObject: JSON.stringify(taskObject), csrftoken: window.csrf_token },
            dispatch,
            () => {
                updateClaimedTicket(ticketDataFlags.ticketId);
                refreshTicketList({
                    dispatch,
                    listApiType,
                    defaultListAPI,
                    listAPI,
                    filteredListAPI,
                });
            },
        );
    };

    const completeTask = async (taskId: string, actTaskId: string, processInstId: string) => {
        ticketDataFlags.actTask = actTaskId;
        ticketDataFlags.actProInstId = processInstId;
        ticketDataFlags.tkmTaskId = taskId;

        handleApiResponse(
            getTaskFromPropertiesAPI,
            { taskId: actTaskId, csrftoken: window.csrf_token },
            dispatch,
            () => {
                updateData();
                onCompleteTicketOpen();
            },
            () => {
                const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchTaskFromProperties' });
                dispatch({
                    type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                    payload: {
                        isOpen: true,
                        errorMessage: errorMessage,
                        modalTitle: 'ticketList.modal.errorModal.header.error',
                    },
                });
            },
        );
    };

    const parseTicketActiveTaskData = async (response: any[], action: CompleteTicketAction) => {
        for (const rowHash of response) {
            const {
                TaskId: taskId,
                ActivitiTaskId: activitiTaskId,
                ProcessInstanceId: processInstanceId,
                Status: taskStatus,
                TaskName: taskName,
                IsAuthorized: isAuthorized,
            } = rowHash;

            if (
                [CompleteTicketAction.COMPLETE, CompleteTicketAction.INTERNAL, CompleteTicketAction.REQUESTOR].includes(
                    action,
                )
            ) {
                handleApiResponse(
                    encTaskAPI,
                    { id: activitiTaskId },
                    dispatch,
                    (data: any) => completeTask(taskId, data.encryptedId, processInstanceId),
                    () => {
                        const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchEncodedTask' });
                        dispatch({
                            type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                            payload: {
                                isOpen: true,
                                errorMessage: errorMessage,
                                modalTitle: 'ticketList.modal.errorModal.header.error',
                            },
                        });
                    },
                );
            }

            if (action === 'claim') {
                claimTask(taskId, activitiTaskId);
            }
        }
    };

    const parseVerifyTicketActiveTaskData = async (response: any[], tktAttachments: any) => {
        const popUpHeader = intl.formatMessage({ id: 'ticketList.modal.verifyCompleteModal.header.verifyDoc' });
        const lblVerifyDocument = 'Verify Document';

        for (const rowHash of response) {
            const {
                TaskId: taskId,
                ActivitiTaskId: activitiTaskId,
                ProcessInstanceId: processInstanceId,
                Status: taskStatus,
                TaskName: taskName,
            } = rowHash;
            ticketDataFlags.taskId = taskId;
            ticketDataFlags.processInstanceId = processInstanceId;

            if (taskStatus === taskStatusTypes.CLAIMED) {
                handleApiResponse(
                    encTaskAPI,
                    { id: activitiTaskId },
                    dispatch,
                    (data: any) => {
                        ticketDataFlags.actTask = data.encryptedId;
                        ticketDataFlags.taskNameContent = taskName === lblVerifyDocument ? popUpHeader : taskName;
                        ticketDataFlags.activitiTaskId = data.encryptedId;
                        updateData();
                        onCompleteVerifyTicketOpen();
                    },
                    () => {
                        const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchEncodedTask' });
                        dispatch({
                            type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                            payload: {
                                isOpen: true,
                                errorMessage: errorMessage,
                                modalTitle: 'ticketList.modal.errorModal.header.error',
                            },
                        });
                    },
                );
            }

            if (taskStatus === taskStatusTypes.UNCLAIMED) {
                claimTask(taskId, activitiTaskId);
            }
        }
    };

    const getTicketActiveTaskDataVerifyDoc = async (isCommentReq: boolean, ticketAttachments: any) => {
        ticketDataFlags.internalComments = isCommentReq && ticketDataFlags.validateVerifyComments !== validateTypes.N;

        handleApiResponse(
            userTicketTaskListAPI,
            { ticketId: ticketDataFlags.ticketIdVerify, csrftoken: window.csrf_token },
            dispatch,
            (data: any) => parseVerifyTicketActiveTaskData(data, ticketAttachments),
            () => {
                const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchUserTask' });
                dispatch({
                    type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                    payload: {
                        isOpen: true,
                        errorMessage: errorMessage,
                        modalTitle: 'ticketList.modal.errorModal.header.error',
                    },
                });
            },
        );
    };

    const getTicketActiveTaskData = async (action: CompleteTicketAction) => {
        ticketDataFlags.isCommentRequired = validateComments === validateTypes.N ? false : true;

        const setTaskHeaders = () => {
            if ([CompleteTicketAction.INTERNAL, CompleteTicketAction.REQUESTOR].includes(action)) {
                ticketDataFlags.taskHeader = `Awaiting ${action.charAt(0).toUpperCase() + action.slice(1)}`;
                ticketDataFlags.ticketListtaskCompleteBtn = ticketDataFlags.taskHeader;
            } else if (action === CompleteTicketAction.COMPLETE) {
                ticketDataFlags.taskHeader = intl.formatMessage({ id: 'ticketList.modal.completeTicketModal.header' });
                ticketDataFlags.ticketListtaskCompleteBtn = intl.formatMessage({
                    id: 'ticketList.modal.completeTicketModal.button.taskComplete',
                });
            }
        };

        setTaskHeaders();

        handleApiResponse(
            userTicketTaskListAPI,
            { ticketId: ticketDataFlags.ticketIdVerify, csrftoken: window.csrf_token },
            dispatch,
            (data: any) => parseTicketActiveTaskData(data, action),
            () => {
                const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchUserTask' });
                dispatch({
                    type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                    payload: {
                        isOpen: true,
                        errorMessage: errorMessage,
                        modalTitle: 'ticketList.modal.errorModal.header.error',
                    },
                });
            },
        );
    };

    const getTicketAttachments = async (ticketId: string) => {
        handleApiResponse(
            ticketAttachmentsApi,
            { ticketId: ticketId, ticketCompleteFlow: true },
            dispatch,
            (data: any) => getTicketActiveTaskDataVerifyDoc(false, data),
            () => getTicketActiveTaskDataVerifyDoc(false, []),
        );
    };

    const completeTicket = async (
        ticketId: string,
        ticketCategory: string | undefined,
        action: CompleteTicketAction,
    ) => {
        ticketDataFlags.ticketId = ticketId;
        ticketDataFlags.ticketIdVerify = ticketId;
        ticketDataFlags.action = action;

        if (
            ticketCategory === ticketCategoryOptions.DOCUMENT_VERIFICATION &&
            ![CompleteTicketAction.INTERNAL, CompleteTicketAction.CLAIM, CompleteTicketAction.REQUESTOR].includes(
                action,
            )
        ) {
            ticketDataFlags.validateVerifyComments = validateTypes.Y;
            await getTicketAttachments(ticketId);
        } else {
            ticketDataFlags.validateComments = [CompleteTicketAction.INTERNAL, CompleteTicketAction.REQUESTOR].includes(
                action,
            )
                ? validateTypes.Y
                : validateTypes.N;
            if (
                ticketCategory === ticketCategoryOptions.QLE &&
                ![CompleteTicketAction.INTERNAL, CompleteTicketAction.CLAIM, CompleteTicketAction.REQUESTOR].includes(
                    action,
                )
            ) {
                await getTicketAttachments(ticketId);
            } else {
                await getTicketActiveTaskData(action);
            }
        }
    };

    const reassignTicket = async (actionType, ticketid, type, subType, workGroup, assigneeName, queueId) => {
        ticketDataFlags.reassignHeader =
            actionType === 'Reassign'
                ? 'ticketList.modal.reassignTicketModal.headerReassign'
                : 'ticketList.modal.reassignTicketModal.headerAssign';

        let ticketSubType = tkmWorkflows?.type;
        let ticketQueueName = tkmQueues?.name;
        let ticketQueueID = tkmQueues?.id;
        let ticketType = tkmWorkflows?.category;
        let ticketTypeId = tkmWorkflows?.id;
        let assignee = quickAction?.assignee;

        if (workGroup != undefined && workGroup !== '' && queueId !== undefined && queueId !== '') {
            ticketQueueName = workGroup;
            ticketQueueID = queueId;
        }
        if (subType !== undefined && subType !== '') {
            ticketSubType = subType;
        }
        if (type !== undefined && type !== '') {
            ticketType = type;
        }
        if (actionType === 'Reassign') {
            assignee = assigneeName;
        }
        if (typeof actionType === 'undefined') {
            actionType = '';
        }
        if (actionType === 'Assign') {
            assignee = '';
        }

        handleApiResponse(
            ticketActiveTaskAPI,
            { ticketId: ticketid, includeInsurerReturn: true },
            dispatch,
            (data: any) => reassignTicketWithTask(data, ticketQueueID, actionType, assignee, ticketid, ticketQueueName),
            () => {
                const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchTicketActiveTask' });
                dispatch({
                    type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                    payload: {
                        isOpen: true,
                        errorMessage: errorMessage,
                        modalTitle: 'ticketList.modal.errorModal.header.error',
                    },
                });
            },
        );
    };

    const reassignTicketWithTask = async (
        data: any,
        ticketQueueID: string,
        actionType: string,
        assignee: string,
        ticketid: string,
        ticketQueueName: string,
    ) => {
        ticketDataFlags.taskClaimed = data && data['Task_Status'] === taskStatusTypes.CLAIMED ? true : false;
        ticketDataFlags.taskId = data && data['Task_Id'];
        ticketDataFlags.queueId = ticketQueueID;
        ticketDataFlags.action = actionType;
        ticketDataFlags.assignee = assignee;
        ticketDataFlags.ticketId = ticketid;
        handleApiResponse(
            allUsersForQueueAPI,
            { qName: ticketQueueName },
            dispatch,
            () => {
                updateData();
                onReassignTicketOpen();
            },
            () => {
                const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchAllUsersForQueue' });
                dispatch({
                    type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                    payload: {
                        isOpen: true,
                        errorMessage: errorMessage,
                        modalTitle: 'ticketList.modal.errorModal.header.error',
                    },
                });
            },
        );
    };

    const reopenTicket = (ticketId: string) => {
        dispatch({
            type: TICKET_LIST_ACTION_TYPES.SET_MODALS_DATA,
            payload: { ...modalData, ticketId: ticketId },
        });
        onReopenTicketOpen();
    };

    const cancelTicket = (ticketid: string) => {
        dispatch({
            type: TICKET_LIST_ACTION_TYPES.SET_MODALS_DATA,
            payload: { ...modalData, ticketId: ticketid },
        });
        onCancelTicketOpen();
    };

    const viewDetails = (encryptedId: string) => {
        window.location.href = getViewDetailUrl(encryptedId);
    };

    const editSummary = (encryptedId: string) => {
        const url = new URL(
            configs.endpoints.TICKETS_LIST_EDIT_TICKET.processReplacements({ encryptedId: encryptedId }),
            window.location.origin,
        );
        window.location.href = url.toString();
    };

    const updateData = useCallback(() => {
        dispatch({
            type: TICKET_LIST_ACTION_TYPES.SET_MODALS_DATA,
            payload: {
                ...modalData,
                taskVerifyName: ticketDataFlags.taskNameContent,
                ticketId: ticketDataFlags.ticketId,
                ticketIdVerify: ticketDataFlags.ticketIdVerify,
                action: ticketDataFlags.action,
                tkmTaskId: ticketDataFlags.tkmTaskId,
                actProInstId: ticketDataFlags.actProInstId,
                actTask: ticketDataFlags.actTask,
                taskHeader: ticketDataFlags.taskHeader,
                ticketListtaskCompleteBtn: ticketDataFlags.ticketListtaskCompleteBtn,
                reassignHeader: ticketDataFlags.reassignHeader,
                taskId: ticketDataFlags.taskId,
                taskClaimed: ticketDataFlags.taskClaimed,
                queueId: ticketDataFlags.queueId,
                assignee: ticketDataFlags.assignee,
                processInstanceId: ticketDataFlags.processInstanceId,
                activitiTaskId: ticketDataFlags.activitiTaskId,
            },
        });

        dispatch({
            type: TICKET_LIST_ACTION_TYPES.SET_MODAL_FLAGS,
            payload: {
                ...modalFlags,
                validateComments: ticketDataFlags.validateComments,
                validateVerifyComments: ticketDataFlags.validateVerifyComments,
                internalComments: ticketDataFlags.internalComments,
                isCommentRequired: ticketDataFlags.isCommentRequired,
                taskClaimed: ticketDataFlags.taskClaimed,
            },
        });
    }, [ticketDataFlags]);

    return {
        completeTicket,
        reassignTicket,
        cancelTicket,
        viewDetails,
        editSummary,
        reopenTicket,
    };
};
